import * as React from 'react'

import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd'
import ItemTypes from './ItemTypes'
import { XYCoord } from 'dnd-core'

import {
  Box,
  Button,
  Typography,
  Icon,
  Collapse,
  Tooltip,
  styled,
} from '@design/pt-ui'
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material'
import FormasetInformationModal from '../FormatsetInformationModal'

import * as actions from '../../model/project/actions'
import { Project } from '../../interfaces/entities/project'
import { Formatset, FormatsetId } from '../../interfaces/entities/formatset'
import { Creative, CreativeAction } from '../../interfaces/entities/creative'
import ConfirmationDialog from '../ConfirmationDialog'
import FormatsetForm from '../FormatsetForm'
import CreativesGrid from './CreativesGrid'
import { IProjectService } from '../../interfaces/services/projects'
import { User } from '../../interfaces/entities/user'
import { IFilesService } from '../../interfaces/services/files'

import DI from '../../app/di'
import { translateWithUserLocale } from '../../utils/i18n'
import CollapseButton from '../CollapseButton'

const HeaderItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: theme.spacing(1),
  marginRight: theme.spacing(2),
  '&:last-child': {
    borderRight: 'none',
  },
}))

interface IFormatsetHeader {
  formatset: Formatset
  project: Project
  user: User
  handleButton: any
  onRemoveFormatSet: (formatsetId: FormatsetId) => any
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
}

const FormatsetHeader = (props: IFormatsetHeader) => {
  const filesService = DI.get('FilesService') as IFilesService
  const [state, setState] = React.useState({
    open: false,
    removeFormatSetDialog: false,
  })

  const openConfirmationDlg = () => {
    setState((state) => ({
      ...state,
      removeFormatSetDialog: true,
    }))
  }

  const closeConfirmationDlg = () => {
    setState((state) => ({
      ...state,
      removeFormatSetDialog: false,
    }))
  }

  const onRemoveFormatset = () => {
    props.onRemoveFormatSet(props.formatset.id)
    setState((state) => ({
      ...state,
      removeFormatSetDialog: false,
    }))
  }

  const onFormatSetSaved = () => {
    const store = DI.get('store')
    store.dispatch(actions.getProject(props.project.id))
  }

  return (
    <Box sx={{ padding: 0.5, paddingLeft: 0, textAlign: 'left' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'start',
          marginTop: 2,
          marginBottom: 2,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'row',
            alignItems: 'center',
            flex: 1,
          }}>
          {props.handleButton}
          <HeaderItem>
            <Typography variant="subtitle1" align="left">
              {props.formatset.name}
            </Typography>
          </HeaderItem>
          {props.formatset.invalid ? (
            <Tooltip
              title={translateWithUserLocale('formatset_outdated.tooltip')}>
              <Icon
                sx={{
                  fontSize: 44,
                  color: 'custom.statusIcon.outdated',
                  marginRight: 2,
                }}>
                timelapse
              </Icon>
            </Tooltip>
          ) : null}

          <FormasetInformationModal formatset={props.formatset} />

          {!props.user.groups.includes('client') ? (
            <HeaderItem>
              <FormatsetForm
                activator={
                  props.project.status.name === 'archived' ||
                  props.formatset.invalid ? (
                    ''
                  ) : (
                    <Button
                      title={translateWithUserLocale(
                        'project.update_formatset',
                      )}
                      color="inherit">
                      <Icon>edit_icon</Icon>
                    </Button>
                  )
                }
                onSave={async () => {
                  onFormatSetSaved()
                }}
                formatSetName={props.formatset.name}
                formatsetId={props.formatset.id}
                restricted={props.formatset.creatives.length > 0}
                projectId={props.project.id}
                platform={props.formatset.type}
              />
              {props.formatset.creatives.length > 0 &&
                props.formatset.type === 'facebook' &&
                props.formatset.creatives.some(
                  (creative) => creative.status.name === 'sent_to_agency',
                ) && (
                  <a
                    style={{ textDecoration: 'none' }}
                    href={filesService.getDownloadFormatsetZipUrl(
                      props.formatset.id,
                    )}>
                    <Button
                      size="medium"
                      title={translateWithUserLocale(
                        'project.download_formatset',
                      )}
                      sx={{
                        color: 'text.primary',
                      }}>
                      <Icon fontSize="small">save_alt</Icon>
                    </Button>
                  </a>
                )}
              <ConfirmationDialog
                title={translateWithUserLocale('project.delete_formatset')}
                open={state.removeFormatSetDialog}
                onConfirm={onRemoveFormatset}
                onClose={closeConfirmationDlg}
                message={translateWithUserLocale(
                  'project.delete_formatset_message',
                  {
                    formatsetName: props.formatset.name,
                  },
                )}
              />
              {props.formatset.creatives.length === 0 && (
                <Button
                  title={translateWithUserLocale('remove_formatset')}
                  onClick={openConfirmationDlg}
                  color="inherit">
                  <Icon>delete_outlined</Icon>
                </Button>
              )}
            </HeaderItem>
          ) : null}
        </Box>
        <CollapseButton
          collapsed={props.collapsed}
          onClick={() => props.setCollapsed(!props.collapsed)}
        />
      </Box>
    </Box>
  )
}

type FormatsetComponentProps = {
  formatset: Formatset
  project: Project
  user: User
  id: any
  index: number
  moveFormatset: (
    dragIndex: number,
    hoverIndex: number,
    platform: string,
  ) => void
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  doCreativeAction: (creative: Creative, action: CreativeAction) => void
  onRemoveFormatSet: (formatsetId: FormatsetId) => any
}

interface DragItem {
  index: number
  id: string
  type: string
}

export const FormatsetComponent = (props: FormatsetComponentProps) => {
  const { collapsed, setCollapsed, moveFormatset, index, id } = props

  const projectService = DI.get('ProjectService') as IProjectService

  const ref = React.useRef<HTMLDivElement>(null)

  const [, drop] = useDrop<DragItem>({
    accept: ItemTypes.FORMATSET,
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current!.getBoundingClientRect()

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      const clientOffset = monitor.getClientOffset()

      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      moveFormatset(dragIndex, hoverIndex, props.formatset.type)

      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.FORMATSET,
    item: { type: ItemTypes.FORMATSET, id, index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item: any, monitor: any) => {
      projectService.updateFormatsetPosition(item.id, item.index + 1)
    },
  })

  const opacity = isDragging ? 0.4 : 1

  drag(drop(ref))

  return (
    <div ref={ref} style={{ opacity, transform: 'translate3d(0, 0, 0)' }}>
      <FormatsetHeader
        formatset={props.formatset}
        project={props.project}
        user={props.user}
        onRemoveFormatSet={props.onRemoveFormatSet}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        handleButton={
          <div
            ref={(node) => {
              drag(node)
            }}
            style={{ cursor: 'move', display: 'flex', marginRight: 15 }}>
            <DragIndicatorIcon />
          </div>
        }
      />
      <Collapse in={!collapsed}>
        <CreativesGrid
          formatset={props.formatset}
          user={props.user}
          doCreativeAction={props.doCreativeAction}
          project={props.project}
        />
      </Collapse>
    </div>
  )
}
